/**
 * MCP API Key Service
 *
 * Manages API keys for the MCP server.
 * Uses Firebase ID tokens for authentication against /api/keys endpoints.
 */
import firebase from "firebase/app";

// API base URL — MCP server on app.seeker.place (proxied via Apache to localhost:3001)
const API_BASE = "https://app.seeker.place/api/keys";

/**
 * Get the current user's Firebase ID token.
 * @returns {Promise<string>} ID token
 */
async function getIdToken() {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }
  return user.getIdToken();
}

/**
 * List all API keys for the current user.
 * @returns {Promise<Array>} Array of key objects { keyHash, name, keyPrefix, created_at, last_used, active }
 */
export async function listApiKeys() {
  const token = await getIdToken();

  const response = await fetch(API_BASE, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || `Failed to list keys (${response.status})`);
  }

  const data = await response.json();
  return data.keys || [];
}

/**
 * Create a new API key.
 * @param {string} name - Label for the key (e.g. "Cursor", "OpenClaw bot")
 * @returns {Promise<{rawKey: string, keyHash: string, name: string}>} The raw key (shown only once!)
 */
export async function createApiKey(name) {
  const token = await getIdToken();

  const response = await fetch(API_BASE, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message || error.error || `Failed to create key (${response.status})`
    );
  }

  return response.json();
}

/**
 * Delete (revoke) an API key.
 * @param {string} keyHash - The key hash to revoke
 * @returns {Promise<void>}
 */
export async function deleteApiKey(keyHash) {
  const token = await getIdToken();

  const response = await fetch(`${API_BASE}/${keyHash}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message || error.error || `Failed to delete key (${response.status})`
    );
  }
}
